import {
  action,
  computed,
  observable,
} from 'mobx';
import {
  AdmixDistribution,
  BatchAction,
  BatchMode,
  ConstituentType,
  JogType,
  MaterialPosition,
  MaterialType,
  MeteredMaterialType,
  WeighedMaterialType,
  UnitType,
} from '../constants/MaterialModels';
import startCase from 'lodash/startCase';
import moment from 'moment-timezone';
import { WebsocketEvent } from '../constants/WebsocketEvent';
import logger from '../utils/logger';
import { toNumber } from '../utils/numbers';
import { MaterialDisplay } from '../constants/Configuration';
import { loadRequests } from '../utils/async';

class MaterialStore {
  @observable
    weighedMaterialType = Object.keys(WeighedMaterialType).map(v => { return { value: WeighedMaterialType[v], text: startCase(v) };});
  @observable
    meteredMaterialType = Object.keys(MeteredMaterialType).map(v => { return { value: MeteredMaterialType[v], text: startCase(v) };});
  @observable
    unit = [];
  @observable
    batchMode = Object.keys(BatchMode).map(v => { return { value: BatchMode[v], text: v };});
  @observable
    materialPosition = Object.keys(MaterialPosition).map(v => { return { value: MaterialPosition[v], text: v };});
  @observable
    materialDetails = {};
  @observable
    jogTables = {};
  batchAction = Object.keys(BatchAction).map(v => { return { value: BatchAction[v], name: startCase(v) };});
  jogType = Object.keys(JogType).map(v => { return { value: JogType[v], name: startCase(v) };});
  admixDistribution = Object.keys(AdmixDistribution).map(v => { return { value: v, name: startCase(v) };});
  assignment = [
    {
      value: MaterialType.Weighed,
      text: MaterialType.Weighed,
    },
    {
      value: MaterialType.Metered,
      text: MaterialType.Metered,
    },
    {
      value: MaterialType.None,
      text: 'Unassigned',
    },
  ];

  @observable
    materials = [];
  @observable
    materialsWithDetails = [];
  @observable
    materialDetailsLoaded = false;
  @observable
    reasonCode = [];
  @observable
    inventoryUsageDateFrom = null;
  @observable
    inventoryUsageDateTo = null;
  @observable
    inventoryUsage = [];
  @observable
    vendor = [];

  constructor(rootStore) {
    this.rootStore = rootStore;
    this.setDates();
  }

  get api() {
    return this.rootStore.newAuthStore;
  }

  get username() {
    return this.api.user?.name;
  }

  get plantId() {
    return this.rootStore.locationsStore.plantId;
  }

  get locationId() {
    return this.rootStore.locationsStore.locationId;
  }

  get timezone() {
    return this.rootStore.settingsStore.configuration?.ianaTimeZone;
  }

  @computed
  get weighUnit() {
    const units = new Set(['lb', 'kg', 'gm', 'oz']);
    return this.unit
      .filter(u => u.type === UnitType.Weight && units.has(String(u.symbol).toLowerCase()));
  }

  @computed
  get scaleUnits() {
    const extraUnits = new Set(['ga']);
    return [
      ...this.weighUnit,
      ...this.unit
        .filter(u => u.type === UnitType.WetVolume && extraUnits.has(String(u.symbol).toLowerCase())),
    ];
  }

  @computed
  get weighedInventoryUnit() {
    const units = new Set(['lb', 'kg', 'gm', 'tn', 'to', 'oz']);
    return this.unit
      .filter(u => u.type === UnitType.Weight && units.has(String(u.symbol).toLowerCase()));
  }

  @computed
  get meterUnit() {
    return this.unit.filter(u => u.type === UnitType.WetVolume);
  }

  @computed
  get meteredInventoryUnit() {
    const units = new Set(['ga', 'oz', 'li', 'ml']);
    return this.unit
      .filter(u => u.type === UnitType.WetVolume && units.has(String(u.symbol).toLowerCase()));
  }

  @computed
  get assignedMaterials() {
    return this.materials.filter(v => v.materialDetailId);
  }

  @computed
  get materialsWithHistory() {
    return this.materials.filter(v => v.inventoryItemId);
  }

  setDates() {
    if (this.timezone) {
      this.inventoryUsageDateFrom = moment()
        .tz(this.timezone)
        .startOf('month');
      this.inventoryUsageDateTo = moment()
        .tz(this.timezone)
        .endOf('month');
      return;
    }

    setTimeout(() => {
      this.setDates();
    }, 1000);
  }

  setBatchMode() {
    const { configuration } = this.rootStore.settingsStore;
    const actions = Object.keys(BatchMode).map(v => { return { value: BatchMode[v], text: v };});

    this.batchMode = !configuration.manualFeed
      ? actions.filter(v => v.value !== BatchMode.Manual)
      : actions;
  }

  getMaterialByItemId = (itemId, fromAllMaterials = false) => {
    let materials;

    if (fromAllMaterials) {
      materials = this.materials;
    } else {
      materials = this.assignedMaterials;
    }

    return materials.find(v => Number(v.itemId) === Number(itemId));
  };

  getMaterialByItemCode = (itemCode) => {
    return this.materialsWithDetails.find(v => v.itemCode && v.itemCode === itemCode);
  };

  getMaterialByBatchCode = (batchCode) => {
    return this.assignedMaterials.find(v => v.batchCode && v.batchCode === batchCode);
  };

  getMaterialByInventoryId = id => this.materials.find(m => Number(m.inventoryItemId) === Number(id));

  getMaterialUnitSymbol = itemId => {
    const material = this.getMaterialByItemId(itemId);
    const details = material ? this.materialDetails[material.materialDetailId] : null;

    return details ? details.itemBatchUnitSymbol : '';
  };

  getConstituentName(item) {
    const { configuration } = this.rootStore.settingsStore;

    if (configuration.materialDisplay === MaterialDisplay.ShortDescription) {
      return configuration.useBatchCodes && item.batchShortDescription ? item.batchShortDescription : item.constituentItemShortDescription;
    } else {
      return configuration.useBatchCodes && item.batchCode ? item.batchCode : item.constituentItemCode;
    }

  }

  getConstituentUnitSymbol(constituent) {
    const material = constituent.batchCode ? this.getMaterialByBatchCode(constituent.batchCode) : this.getMaterialByItemId(constituent.constituentItemId);
    return material ? this.getMaterialUnitSymbol(material.itemId) : constituent.unitSymbol;
  }

  getConstituentMaterialDetails(constituent) {
    const material = this.getMaterialByItemId(constituent.constituentItemId);
    return material ? this.materialDetails[material.materialDetailId] : null;
  }

  getMaterialName(material) {
    const { configuration } = this.rootStore.settingsStore;

    if (configuration.materialDisplay === MaterialDisplay.ShortDescription) {
      return configuration.useBatchCodes && material.batchShortDescription ? material.batchShortDescription : material.itemShortDescription;
    } else {
      return configuration.useBatchCodes && material.batchCode ? material.batchCode : material.code || material.itemCode;
    }

  }

  getMaterialNameByItemId(itemId) {
    const { configuration } = this.rootStore.settingsStore;
    const material = this.getMaterialByItemId(itemId, true) || {};

    if (configuration.materialDisplay === MaterialDisplay.ShortDescription) {
      return configuration.useBatchCodes && material.batchShortDescription ? material.batchShortDescription : material.itemShortDescription;
    } else {
      return configuration.useBatchCodes && material.batchCode ? material.batchCode : material.code;
    }

  }

  shouldHideInactiveBin(binId) {
    const material = this.getMaterialForBin(binId);

    if (!material) {
      return false;
    }

    const md = this.materialDetails[material.materialDetailId];
    const b = material.bins.find(v => this.binMatchesId(v, binId));

    return md && (!!md.showOnlyActiveBins || !!md.aggregateMode) && b && (!b.isActive && !b.isVisible);
  }

  isMaterialForBinVisible(binId) {
    const material = this.getMaterialForBin(binId);

    if (!material) {
      return true;
    }

    return !!material.visible;
  }

  isOtherMaterialWithTheSameOutputVisible(material) {
    const { devicesStore } = this.rootStore;
    const bin = devicesStore.getPrimaryBin(material);
    let isVisible = false;

    if (!bin) {
      return isVisible;
    }

    devicesStore.binsWithTheSameOutput.forEach(v => {
      if (v.output === bin.gateOnePNo) {
        let m = this.getMaterialForBin(v.id);
        const binIsActive = m?.bins?.some(b => b.isActive && this.binMatchesId(b, v.id));

        if (m?.visible && binIsActive && material && material.itemId !== m.itemId) {
          const materialName = this.getMaterialName(m);
          logger.log(`${materialName} is visible!`);

          isVisible = true;
        }
      }
    });

    return isVisible;
  }

  getMaterialWithTheSamePosition(material = {}) {
    return this.materials.filter(v => v.scaleId && v.scaleId === material.scaleId
      && v.position && v.position === material.position && v.itemId !== material.itemId);
  }

  switchMaterialVisibilityForDecumScale(material = {}) {
    const otherMaterials = this.getMaterialWithTheSamePosition(material);

    otherMaterials.forEach(m => {
      const md = this.materialDetails[m.materialDetailId];
      this.saveMaterialDetail(m, { ...md, visible: false });
    });

    const md = this.materialDetails[material.materialDetailId];
    setTimeout(() => {
      this.saveMaterialDetail(material, { ...md, visible: true });
    }, 500);
  }

  getMaterialsWithTheSameOutput(binId) {
    const { devicesStore } = this.rootStore;
    const bin = devicesStore.getDeviceById(binId);
    let materials = [];

    if (!bin) {
      return materials;
    }

    const material = this.getMaterialForBin(binId);

    if (!material) {
      return materials;
    }

    devicesStore.binsWithTheSameOutput.forEach(v => {
      if (v.output === bin.gateOnePNo) {
        let m = this.getMaterialForBin(v.id);

        if (m && m.code !== material.code) {
          materials.push(m);
        }
      }
    });

    return materials;
  }

  makeMaterialVisible(material, binId) {
    const { devicesStore } = this.rootStore;
    const outputs = [];
    const md = this.materialDetails[material.materialDetailId];
    const aggMode = md && md.aggregateMode;

    if (aggMode) {
      const binOutput = devicesStore.binsWithTheSameOutput.find(v => v.id === binId);

      if (!binId || !binOutput) {
        return;
      }

      this.weighedMaterials.forEach(m => {
        const md = this.materialDetails[m.materialDetailId];

        if (md && md.bins) {
          md.bins.forEach(b => {
            const bin = devicesStore.getDeviceById(b.binId);

            if (bin && bin.gateOnePNo === binOutput.output) {
              if (m.code === material.code) {
                b.isActive = true;
                b.isVisible = true;

                const primary = md.bins.find(v => v.isPrimary);
                if (primary && !primary.isActive) {
                  primary.isPrimary = false;
                  b.isPrimary = true;
                }

                md.visible = true;

                logger.log('aggMode active', m.code, bin.id, bin.gateOnePNo);

              } else {
                b.isActive = false;
                b.isVisible = false;

                if (b.isPrimary) {
                  let b2 = md.bins.find(v => v.isActive && !v.isPrimary);
                  if (b2) {
                    b.isPrimary = false;
                    b2.isPrimary = true;
                  }
                }

                logger.log('aggMode inactive', m.code, bin.id, bin.gateOnePNo);
              }

              this.saveMaterialDetail(m, { ...md });
            }
          });
        }
      });
      return;
    }

    material.bins.forEach(b => {
      const bin = devicesStore.getDeviceById(b.binId);
      if (bin && bin.gateOnePNo) {
        outputs.push(bin.gateOnePNo);
      }
    });

    if (!outputs.length) {
      return;
    }

    let changedMaterials = [];
    devicesStore.binsWithTheSameOutput.forEach(v => {
      if (outputs.includes(v.output)) {
        let m = this.getMaterialForBin(v.id);

        if (m && material.itemId !== m.itemId && !changedMaterials.includes(m.itemId)) {
          const md = this.materialDetails[m.materialDetailId];
          if (md && m.visible) {
            changedMaterials.push(m.itemId);
            this.saveMaterialDetail(m, { ...md, visible: false });
          }
        }
      }
    });

    const materialDetails = this.materialDetails[material.materialDetailId];
    if (materialDetails && !materialDetails.visible) {
      this.saveMaterialDetail(material, { ...materialDetails, visible: true });
    }

  }

  binMatchesId(bin, id) {
    return bin.binId === id || bin.linkedBinId === id;
  }

  getMaterialForBin(binId) {
    let material = null;

    this.materialsWithDetails.forEach(m => {
      if (m.bins?.find(v => this.binMatchesId(v, binId))) {
        material = m;
      }
    });

    return material;
  }

  getUnitSymbolByUnitId(unitId) {
    const unit = this.unit.find(v => v.id === unitId);
    return unit ? unit.symbol || unit.name : '';
  }

  isDeviceSetToConstituent(constituent = {}) {
    const material = constituent?.batchCode
      ? this.getMaterialByBatchCode(constituent.batchCode)
      : this.getMaterialByItemId(constituent.constituentItemId);
    const md = material ? this.materialDetails[material.materialDetailId] : null;

    if (md && material.assignment === MaterialType.Weighed) {
      return !!md.bins.length;
    } else if (md && material.assignment === MaterialType.Metered) {
      return !!md.pumpId;
    }

    return false;
  }

  /**
   * A material in Skip batch mode is put on the ticket at its target without being fed, so it can be batched without a
   * bin or pump. A skip material that has a device keeps going through that device's feed.
   */
  isSkipMaterialWithoutDevice(constituent = {}) {
    const material = constituent?.batchCode
      ? this.getMaterialByBatchCode(constituent.batchCode)
      : this.getMaterialByItemId(constituent.constituentItemId);
    const md = material ? this.materialDetails[material.materialDetailId] : null;

    return md?.batchMode === BatchMode.Skip && !this.isDeviceSetToConstituent(constituent);
  }

  isDeviceSetToMaterial(materialItemId) {
    const material = this.getMaterialByItemId(materialItemId);
    const md = material ? this.materialDetails[material.materialDetailId] : null;

    if (md && material.assignment === MaterialType.Weighed) {
      if (!md.scaleId) {
        return !!md.bins.length;
      }
      return true;
    } else if (md && material.assignment === MaterialType.Metered) {
      return !!md.pumpId;
    }

    return false;
  }

  isConstituentBinActive(constituent) {
    const material = constituent?.batchCode
      ? this.getMaterialByBatchCode(constituent.batchCode)
      : this.getMaterialByItemId(constituent.constituentItemId);

    if (material?.assignment !== MaterialType.Weighed || material.scaleId) {
      return true;
    }

    return !!material.bins?.some(bin => bin.isActive);
  }

  hasBinOnlyOneMaterialAssigned() {
    let binsIds = [];
    let isOne = true;

    this.weighedMaterials
      .filter(v => v.bins.length)
      .forEach(m => {
        m.bins.forEach(b => {
          if (binsIds.includes(b.binId) && b.isActive) {
            logger.log(`material: ${m.code}, bin #${b.binId} is assigned and active on two different materials`);
            isOne = false;
          }

          if (!binsIds.includes(b.binId) && b.isActive) {
            binsIds.push(b.binId);
          }
        });
      });

    return isOne;
  }

  isConstituentAssigned(constituent = {}) {
    const material = constituent && constituent.batchCode
      ? this.getMaterialByBatchCode(constituent.batchCode)
      : this.getMaterialByItemId(constituent.constituentItemId);
    return material && this.materialDetails[material.materialDetailId];
  }

  isAdmixture(materialId) {
    const md = this.materialsWithDetails.find(v => v.itemId === Number(materialId)) || {};

    if (md.assignment === MaterialType.Metered) {
      return md.type === MeteredMaterialType.Admixture;
    }

    return false;
  };

  isCement(materialId) {
    const md = this.materialsWithDetails.find(v => v.itemId === Number(materialId)) || {};

    return md.assignment === MaterialType.Weighed && md.type === WeighedMaterialType.Cement;
  };

  isCementitious(materialId) {
    const md = this.materialsWithDetails.find(v => v.itemId === Number(materialId)) || {};

    return md.assignment === MaterialType.Weighed && md.type === WeighedMaterialType.Cementitious;
  };

  isAggregate(materialId) {
    const md = this.materialsWithDetails.find(v => v.itemId === Number(materialId)) || {};

    return md.assignment === MaterialType.Weighed &&
      (md.type === WeighedMaterialType.FineAggregate || md.type === WeighedMaterialType.CoarseAggregate);
  };

  isWater(materialId) {
    const md = this.materialsWithDetails.find(v => v.itemId === Number(materialId)) || {};

    return md.type === WeighedMaterialType.Water;
  };

  isSecondWater(materialId) {
    const md = this.materialsWithDetails.find(v => v.itemId === Number(materialId)) || {};

    return md.type === WeighedMaterialType.SecondWater;
  };

  get secondWater() {
    return this.materialsWithDetails.find(md => md.type === WeighedMaterialType.SecondWater);
  };

  get water() {
    return this.materialsWithDetails.find(md => md.type === WeighedMaterialType.Water);
  };

  get allWaters() {
    return this.materialsWithDetails.filter(md => md.type === WeighedMaterialType.Water || md.type === WeighedMaterialType.SecondWater);
  }

  @action
  getConstituentType(materialItemId) {
    const md = this.materialsWithDetails.find(v => v.itemId === Number(materialItemId)) || {};

    if (md.assignment === MaterialType.Weighed) {
      if (md.type === WeighedMaterialType.Cement || md.type === WeighedMaterialType.Cementitious) {
        return ConstituentType.CEMENT;
      } else if (md.type === WeighedMaterialType.FineAggregate || md.type === WeighedMaterialType.CoarseAggregate) {
        return ConstituentType.AGGREGATE;
      } else if (md.type === WeighedMaterialType.Water || md.type === WeighedMaterialType.SecondWater) {
        return ConstituentType.WATER;
      }
    } else if (md.assignment === MaterialType.Metered) {
      if (md.type === MeteredMaterialType.SecondWater || md.type === MeteredMaterialType.Water) {
        return ConstituentType.WATER;
      } else if (md.type === MeteredMaterialType.Admixture) {
        return ConstituentType.ADMIXTURE;
      }
    }

    return null;
  }

  setConstituentTypeAndOrderForMixDesign(mixDesign = []) {
    let md = [];

    mixDesign.forEach((v, i) => {
      md.push({ ...v, type: this.getConstituentType(v.constituentItemId), order: i+1 });
    });

    return md;
  }

  @action
  checkMaterialLevel(materialDetails) {
    if (!materialDetails) {
      return;
    }

    const m = materialDetails;

    if (m && m.alarmOn && m.alarmLevel >= m.currentBalance) {
      const unit = m.itemInventoryUnitSymbol;
      this.rootStore.notifierStore.showWarning({ key: 'Material balance is below!', props: { materialCode: m.itemCode, currentBalance: m.currentBalance, unit, alarmLevel: m.alarmLevel }});
    }
  };

  getGravity(constituent) {
    const material = this.getMaterialByItemId(constituent.constituentItemId);
    const materialDetails = material ? this.materialDetails[material.materialDetailId] || {} : {};

    return materialDetails.specificGravity || 1;
  }

  getScaleGraduationForConstituent(constituent) {
    if (!constituent) {
      return 0;
    }

    const material = constituent.batchCode
      ? this.getMaterialByBatchCode(constituent.batchCode)
      : this.getMaterialByItemId(constituent.constituentItemId || constituent.itemId);
    if (!material) return 0;
    const md = this.materialDetails[material.materialDetailId];
    if (!md) return 0;
    const { devicesStore } = this.rootStore;

    let scaleId = md.scaleId;
    if (!scaleId && md.bins && md.bins.length) {
      const activeBin = md.bins.find(b => b.isActive);
      if (activeBin) {
        const bin = devicesStore.getDeviceById(activeBin.binId);
        scaleId = bin?.scaleId;
      }
    }
    if (!scaleId) return 0;
    const scale = devicesStore.getDeviceById(scaleId);
    return scale?.graduation || 0;
  }

  calculateYield(mixDesign, field = 'quantityNet') {
    let y = 0;

    mixDesign.forEach(v => {
      const g = this.getGravity(v);
      y += v[field]/g;
    });

    return toNumber(y/1000, 3);
  }

  @action
  loadMaterials(withDetails = true) {
    const url = `/api/plant/${this.plantId}/material`;

    return this.api
      .makeAuthorizedRequest(url)
      .then(resp => {
        const materials = resp || [];
        const requests = [];
        const assignedMaterials = materials.filter(v => v.materialDetailId);

        assignedMaterials.forEach(m => {
          if (m.assignment === MaterialType.Weighed || m.assignment === MaterialType.Metered) {
            if (withDetails) {
              const details = { func: this.loadMaterialDetail, args: [m.materialDetailId] };
              requests.push(details);
            }
          }
        });

        if (requests.length) {
          loadRequests(requests).then(() => {
            this.materials = materials;
            this.materialDetailsLoaded = true;
            this.setMaterialsWithDetails();
          }).catch(e => {
            this.rootStore.notifierStore.showError(`Issue loading materials, ${e?.message}`);
          });
        } else {
          this.materials = materials;
          this.materialDetailsLoaded = true;
          this.setMaterialsWithDetails();
        }
      });
  }

  setMaterialsWithDetails() {
    this.materialsWithDetails = this.assignedMaterials.map(m => {
      return { ...m, ...this.materialDetails[m.materialDetailId] };
    });
  }

  @action
    loadMaterialDetail = (materialDetailId) => {
      const url = `/api/plant/${this.plantId}/material/${materialDetailId}`;

      return this.api
        .makeAuthorizedRequest(
          url
        )
        .then(detail => {
          this.materialDetails[materialDetailId] = detail;
          this.updateMaterial(detail);
          this.checkMaterialLevel(detail);

          return detail;
        });
    };

  @action
  loadTypes() {
    this.loadType('weighedMaterialType', 'type')
      .then(() => this.loadType('meteredMaterialType', 'type'))
      .then(() => this.loadType('batchMode', 'type'))
      .then(() => this.loadType('unit'))
      .then(() => this.loadType('vendor'))
      .then(() => this.loadType('reasonCode'));

  }

  @action
  loadType(type, path) {
    if (this[type] && this[type].length) {
      return Promise.resolve(this[type]);
    }

    const url = !path ? `/api/${type}` : `/api/${path}/${type}`;

    this.api.storeDB.init()
      .then(() => this.api.storeDB.getByKey(url))
      .then(data => this[type] = data || []);

    return this.api.makeAuthorizedRequest(url).then(resp => {
      this[type] = resp || [];
    });
  }

  @action
  assignMaterial(material, assignment) {
    return this.api
      .makeAuthorizedRequest(
        `/api/item/${material.itemId}/plant/${this.plantId}/assignment/${assignment}`,
        {},
        'POST'
      )
      .then(() => {
        this.onMaterialUpdate();
      });
  };

  @action
  unassignMaterial(material) {
    return this.api
      .makeAuthorizedRequest(
        `/api/plant/${this.plantId}/material/${material.materialDetailId}/assignment`,
        {},
        'DELETE'
      )
      .then(() => {
        this.onMaterialUpdate();
      });
  };

  @action
  swapBins(binId, material, materialDetails) {
    const { batchStore } = this.rootStore;

    const primaryIndex = materialDetails.bins.findIndex(v => v.isPrimary);
    const nextPrimaryIndex = materialDetails.bins.findIndex(v => v.isActive && !v.isPrimary);

    if (nextPrimaryIndex < 0) {
      return;
    }

    materialDetails.bins[primaryIndex].isPrimary = false;
    materialDetails.bins[primaryIndex].isActive = true;
    materialDetails.bins[nextPrimaryIndex].isPrimary = true;

    this.updateMaterial(materialDetails);

    if (batchStore.isHoldFeedDevice(binId)) {
      const b1 = batchStore.getDeviceStatus(binId);
      const newPrimaryBin = materialDetails.bins.find(v => v.isPrimary);
      const b2 = batchStore.getDeviceStatus(newPrimaryBin.binId);
      batchStore.deviceStatus.set(newPrimaryBin.binId, b1);
      batchStore.deviceStatus.set(binId, b2);
      batchStore.holdFeedDevices.push(newPrimaryBin.binId);
    }

    this.saveMaterialDetail(material, {
      ...materialDetails,
    });
  }

  @action
  toggleBin(binId, material, materialDetails) {
    materialDetails.bins.forEach((v, i) => {
      if (v.binId === binId) {
        materialDetails.bins[i].isActive = !materialDetails.bins[i].isActive;
      }
    });

    this.updateMaterial(materialDetails);

    this.saveMaterialDetail(material, {
      ...materialDetails,
    });
  }

  @action
  saveMaterialDetail(material, values, forceUpdate = false, tryAgain = true) {
    const { connectionStore } = this.rootStore;
    const { materialDetailId, assignment } = material;
    const type = assignment === MaterialType.Weighed ? 'weighed' : 'metered';
    const url = `/api/plant/${this.plantId}/material/${type}/${materialDetailId}`;

    clearTimeout(this[`timer-${materialDetailId}`]);

    if (!connectionStore.isOnline || forceUpdate) {
      this.updateMaterial({ id: materialDetailId, ...values });
      this.saveMaterialDetailsOffline(materialDetailId, values);
    }

    let promise = this.api.makeAuthorizedRequest(url, values, 'PUT');

    if (tryAgain) {
      return promise.then((resp) => {
        this.updateMaterial(resp);
      })
        .catch(() => {
          if (!this[`timer-${materialDetailId}`]) {
            this[`timer-${materialDetailId}`] = setTimeout(() => {
              this.saveMaterialDetail(material, values, forceUpdate, connectionStore.isOnline);
            }, 1000);
          }
        });
    } else {
      return promise.then((resp) => {
        this.updateMaterial(resp);
      });
    }
  };

  updateMaterial(materialDetails = {}) {
    const { notifierStore } = this.rootStore;
    const index = this.materials.findIndex(v => v.itemId === materialDetails.itemId);

    if (index >= 0) {
      this.materials[index] = {
        ...this.materials[index],
        bins: materialDetails.bins,
        position: materialDetails.position,
        scaleId: materialDetails.scaleId,
        pumpId: materialDetails.pumpId,
        visible: materialDetails.visible,
      };
    }

    if (materialDetails?.id && this.materialDetails[materialDetails?.id]) {
      this.materialDetails[materialDetails.id] = materialDetails;
    }

    const index2 = this.materialsWithDetails.findIndex(v => v.materialDetailId === materialDetails.id);
    if (index2 >= 0) {
      this.materialsWithDetails[index2] = { ...this.materialsWithDetails[index2], ...materialDetails };
    }

    if (!this.hasBinOnlyOneMaterialAssigned()) {
      notifierStore.showWarning('One of the bins is assigned and active on two different materials!');
    }

  }

  @action
  saveMaterialDetailsOffline(materialDetailId, data) {
    const url = `/api/plant/${this.plantId}/material/${materialDetailId}`;
    this.api.storeDB.init()
      .then(() => this.api.storeDB.save(url, data));
  }

  @action
  saveMaterialsOffline() {
    const url = `/api/plant/${this.plantId}/material`;
    this.api.storeDB.init()
      .then(() => this.api.storeDB.save(url, this.materials));
  }

  @action
  adjustQuantity(materialId, payload = {}) {
    let url = `/api/item/${materialId}/inventory/plant/${this.plantId}/adjust`;

    return this.api
      .makeAuthorizedRequest(
        url,
        {
          itemId: payload.itemId,
          quantity: payload.quantity,
          binIds: payload.binIds || [],
          date: moment().tz(this.timezone).format(),
          reasonCodeId: payload.reason ? payload.reason.id : null,
          reasonCodeCode: payload.reason ? payload.reason.code: '',
          reasonCodeShortDescription: payload.reason ? payload.reason.shortDescription : '',
          reasonCodeDescription: payload.reason ? payload.reason.description : '',
          setDeviceBalance: true,
          comment: payload.comment || '',
          clearOtherLocationInventory: payload.clearOtherLocationInventory || false,
        },
        'POST'
      )
      .then(data => {
        this.materials.forEach((v, i) => {
          if (v.itemId === data.itemId) {
            this.materials[i].currentBalance = data.currentBalance;
          }
        });
        this.materials = this.materials.slice();
        this.loadMaterials(false);
        return data;
      });
  };

  @action
  updateMaterialInventoryUseForNonBatchProducts(payload) {
    if (payload.products && payload.products.length) {
      payload.products.forEach(v => {
        if (this.isOther(v.itemId)) {
          const material = this.getMaterialByItemId(v.itemId);

          if (material) {
            this.updateMaterialInventoryUse(material, {
              quantity: v.quantity,
              setDeviceBalance: false,
            });
          }
        }
      });
    }
  }

  @action
  updateMaterialInventoryUse(material, payload) {
    const { batchStore, connectionStore } = this.rootStore;
    const { isDemo, maintenanceMode } = batchStore;
    const { isOnline } = connectionStore;

    return this.api
      .makeAuthorizedRequest(
        `/api/plant/${this.plantId}/inventory/${material.inventoryItemId}/use`,
        {
          inventoryItemId: material.inventoryItemId,
          date: payload.date || moment().tz(this.timezone).format(),
          quantity: payload.quantity,
          comment: `${isDemo ? 'DEMO ' : ''}${!isOnline ? 'Offline ' : '' }${maintenanceMode ? 'Maintenance ' : ''}${payload.comment}`,
          ticket: Number.isInteger(Number(payload.ticket)) ? payload.ticket : '',
          isAutomatic: payload.isAutomatic,
          binIds: payload.binIds || [],
          setDeviceBalance: typeof payload.setDeviceBalance === 'boolean' ? payload.setDeviceBalance : true,
        },
        'PUT'
      )
      .then((data = {}) => {
        this.updateMaterialInventoryData(data);
      });
  };

  calculateInventoryOffline(data, material) {
    if (!material) {
      return data;
    }

    const binId = data?.binIds?.[0];
    const deviceBalance = (material.deviceBalance || []).map(d =>
      binId != null && d.deviceId === binId
        ? {
          ...d,
          currentBalance: d.currentBalance - data.quantity,
        }
        : d
    );

    return {
      itemId: material.itemId,
      currentBalance: material.currentBalance - data.quantity,
      deviceBalance,
      usageToday: material.usageToday > 0
        ? material.usageToday + data.quantity
        : material.usageToday - data.quantity,
    };

  };

  updateMaterialInventoryData(data) {
    let material = this.materials.find(v => v.itemId === data.itemId);

    if (!material && data.inventoryItemId) {
      material = this.getMaterialByInventoryId(data.inventoryItemId);
      data = this.calculateInventoryOffline(data, material);
    }

    if (material) {
      material.currentBalance = data.currentBalance;
      material.deviceBalance = data.deviceBalance || [];
      material.usageToday = data.usageToday;

      let md = this.materialDetails[material.materialDetailId];

      if (md) {
        md.currentBalance = data.currentBalance;
        md.deviceBalance = data.deviceBalance || [];
        md.usageToday = data.usageToday;
        this.setMaterialsWithDetails();
        this.saveMaterialDetailsOffline(material.materialDetailId, md);
      }

      this.materials = this.materials.slice();
      this.saveMaterialsOffline();
    }
  }

  @action
  changeMaterialInventoryHistory(data) {
    return this.api
      .makeAuthorizedRequest(
        `/api/plant/${this.plantId}/inventory/history/${data.id}`,
        data,
        'PUT'
      );
  };

  @action
    onMaterialUpdate = () => {
      this.loadMaterials();
    };

  @computed
  get unassignedMaterials() {
    return this.materials.filter(m => m.assignment === MaterialType.None);
  }

  @computed
  get weighedMaterials() {
    return this.materials.filter(m => m.assignment === MaterialType.Weighed);
  }

  @computed
  get meteredMaterials() {
    return this.materials.filter(m => m.assignment === MaterialType.Metered);
  }

  @computed
  get otherMaterials() {
    return this.materials.filter(m => m.assignment === MaterialType.Other);
  }

  isOther(itemId) {
    const material = this.getMaterialByItemId(itemId);
    return material && material.assignment === MaterialType.Other;
  }

  @action
  getReconciliation(month, year, inventoryItemId) {
    return this.api
      .makeAuthorizedRequest(
        `/api/plant/${this.plantId}/inventory/reconciliation`,
        {
          plantId: this.plantId,
          year,
          month,
          inventoryItemId,
        },
        'GET'
      );
  }

  @action
  saveReconciliation(year, month, data) {
    return this.api
      .makeAuthorizedRequest(
        `/api/plant/${this.plantId}/inventory/reconciliation?year=${year}&month=${month}`,
        data,
        'POST'
      );
  }

  @action
  undoReconcile(year, month) {
    return this.api
      .makeAuthorizedRequest(
        `/api/plant/${this.plantId}/inventory/reconciliation?year=${year}&month=${month}`,
        null,
        'DELETE'
      );
  }

  removeMaterialByItemId(itemId) {
    this.materials = this.materials.filter(v => v.itemId !== itemId);
  }

  /**
   * Find a bin for a material that is assigned to a specific scale
   *
   * @param {Object} material - The material to find a bin for
   * @param {number} scaleId - The scale ID to match
   * @returns {Object|null} - The bin object or null if not found
   */
  findBinForMaterialOnScale(material, scaleId) {
    const { devicesStore } = this.rootStore;
    const materialDetails = this.materialDetails[material.materialDetailId];

    if (!materialDetails || !materialDetails.bins || !materialDetails.bins.length) {
      logger.warn('No bins found for material:', material.code);
      return null;
    }

    // Find active bins for this material that are assigned to the target scale
    const availableBins = materialDetails.bins.filter(binAssignment => {
      if (!binAssignment.isActive) {
        return false;
      }

      const bin = devicesStore.getDeviceById(binAssignment.binId);
      if (!bin) {
        logger.warn('Bin not found:', binAssignment.binId);
        return false;
      }

      return String(bin.scaleId) === String(scaleId);
    });

    if (!availableBins.length) {
      logger.warn(`No active bins found for material ${material.code} on scale ${scaleId}`);
      return null;
    }

    // Prefer primary bin, fall back to first available
    const primaryBin = availableBins.find(binAssignment => binAssignment.isPrimary);
    const selectedBinAssignment = primaryBin || availableBins[0];

    const selectedBin = devicesStore.getDeviceById(selectedBinAssignment.binId);
    logger.log('Selected bin for material substitution:', material.code, 'bin:', selectedBin.id, 'scale:', scaleId);

    return selectedBin;
  }

  getDevicesBalance(material = {}) {
    if (!material) {
      return 0;
    }

    if (material.assignment !== MaterialType.Weighed) {
      return material.currentBalance;
    }

    if (!material.deviceBalance || !Array.isArray(material.deviceBalance) || !material.bins) {
      return 0;
    }

    if (material.sumBalanceForLocation) {
      return toNumber(material.deviceBalance.map(v => v.currentBalance).reduce((a, b) => a + b, 0), 2);
    }

    const binIds = new Set([
      ...material.bins.map(v => v.binId),
      ...material.bins.map(v => v.linkedBinId),
    ]);

    const binsBalance = material.deviceBalance.reduce(
      (sum, v) => sum + (binIds.has(v.deviceId) ? v.currentBalance : 0),
      0
    );

    return toNumber(binsBalance, 2);
  }

  getBinBalance(material, binId) {
    if (!material) {
      return 0;
    }

    if (!material.deviceBalance || !Array.isArray(material.deviceBalance)) {
      return 0;
    }

    const data = material.deviceBalance.find(v => v.deviceId === Number(binId));
    return data ? data.currentBalance : 0;
  }

  getActiveBinsBalance(material) {
    if (!material) {
      return 0;
    }

    if (!material.deviceBalance || !Array.isArray(material.deviceBalance) || !material.bins) {
      return 0;
    }

    const activeBins = material.bins.filter(v => v.isActive);
    const binIds = new Set([
      ...activeBins.map(v => v.binId),
      ...activeBins.map(v => v.linkedBinId),
    ]);

    const binsBalance = material.deviceBalance.reduce(
      (sum, v) => sum + (binIds.has(v.deviceId) ? v.currentBalance : 0),
      0
    );

    return toNumber(binsBalance, 2);
  }

  @action
  loadInventoryReport() {
    return this.api
      .makeAuthorizedRequest(`/api/plant/${this.plantId}/inventory/report`,
        {
          'date.from': moment(this.inventoryUsageDateFrom).tz(this.timezone).format(),
          'date.to': moment(this.inventoryUsageDateTo).tz(this.timezone).format(),
        })
      .then(resp => {
        this.inventoryUsage = resp || [];
      });
  }

  startWebSockets = () => {
    const { notifierStore, websocketStore, connectionStore, settingsStore } = this.rootStore;
    this.ws = websocketStore.connection;
    clearTimeout(this.wsTimer);

    if (!this.ws || !connectionStore.isOnline || !websocketStore.isTrulyConnected) {
      this.wsTimer = setTimeout(() => {
        this.startWebSockets();
      }, 2000);
      return;
    }

    this.ws.on(WebsocketEvent.MATERIAL_ADD, resp => {
      logger.log('ws:', WebsocketEvent.MATERIAL_ADD, resp);

      if (resp) {
        this.loadMaterials();
        if (settingsStore.notificationSettings.material) {
          notifierStore.showInfo({ key: 'Material added!', props: { materialCode: resp.itemCode } });
        }
      }
    });

    this.ws.on(WebsocketEvent.MATERIAL_UPDATE, resp => {
      logger.log('ws:', WebsocketEvent.MATERIAL_UPDATE, resp);

      if (resp) {
        const m = this.getMaterialByItemId(resp.itemId);
        if (m && m.materialDetailId) {
          this.loadMaterials(false);
          this.loadMaterialDetail(m.materialDetailId);
        }
        if (settingsStore.notificationSettings.material) {
          notifierStore.showInfo({ key: 'Material updated!', props: { materialCode: resp.itemCode } });
        }
      }
    });

    this.ws.on(WebsocketEvent.MATERIAL_DELETE, resp => {
      logger.log('ws:', WebsocketEvent.MATERIAL_DELETE, resp);

      if (resp) {
        this.loadMaterials(false);
        if (settingsStore.notificationSettings.material) {
          notifierStore.showInfo({
            key: 'Material updated!',
            props: { materialCode: resp.itemCode || resp.itemId || resp.id },
          });
        }
      }
    });

    this.ws.on(WebsocketEvent.MATERIAL_ASSIGN, resp => {
      logger.log('ws:', WebsocketEvent.MATERIAL_ASSIGN, resp);

      if (resp) {
        this.loadMaterials();
        if (settingsStore.notificationSettings.material) {
          notifierStore.showInfo({
            key: 'Material assigned!',
            props: { materialCode: resp.itemCode || resp.itemId || resp.id },
          });
        }
      }
    });

    this.ws.on(WebsocketEvent.MATERIAL_REASSIGN, resp => {
      logger.log('ws:', WebsocketEvent.MATERIAL_REASSIGN, resp);

      if (resp) {
        this.loadMaterials();
        if (settingsStore.notificationSettings.material) {
          notifierStore.showInfo({
            key: 'Material reassigned!',
            props: { materialCode: resp.itemCode || resp.itemId || resp.id },
          });
        }
      }
    });

    this.ws.on(WebsocketEvent.MATERIAL_UNASSIGN, resp => {
      logger.log('ws:', WebsocketEvent.MATERIAL_UNASSIGN, resp);

      if (resp) {
        this.loadMaterials();
        if (settingsStore.notificationSettings.material) {
          notifierStore.showInfo({
            key: 'Material unassigned!',
            props: { materialCode: resp.itemCode || resp.itemId || resp.id },
          });
        }
      }
    });

    this.ws.on(WebsocketEvent.INVENTORY_ITEM_CHANGE, resp => {
      logger.log('ws:', WebsocketEvent.INVENTORY_ITEM_CHANGE, resp);

      if (resp) {
        this.updateMaterialInventoryData(resp);
      }
    });

    this.ws.onclose(this.startWebSockets);
  };
}

export default MaterialStore;
